import React from 'react';
import './BoostDetails.css';
import * as st from '../../core/st';
import GameText from '../utils/GameText';
import { formatSpellLineConditionText } from '../../core/spellUtils';

const HEADERS = {
    type: { fr: 'Boost', en: 'Boost', de: 'Boost', es: 'Boost', pt: 'Boost' },
    stat: { fr: 'Stat', en: 'Stat', de: 'Wert', es: 'Stat', pt: 'Stat' },
    normal: { fr: 'Normal', en: 'Normal', de: 'Normal', es: 'Normal', pt: 'Normal' },
    critical: { fr: 'Critique', en: 'Critical', de: 'Kritisch', es: 'Crítico', pt: 'Crítico' },
};
const TXT_AP = { fr: 'PA', en: 'AP', de: 'AP', es: 'PA', pt: 'PA' };
// A passive is never cast, so it has no cost to quote.
const TXT_PASSIVE = { fr: 'Passif', en: 'Passive', de: 'Passiv', es: 'Pasivo', pt: 'Passivo' };
// Values in the table are per charge; this says how many a boost can hold.
const TXT_MAX_CHARGES = {
    fr: 'Cumulable {n} fois',
    en: 'Stacks {n} times',
    de: '{n}-mal stapelbar',
    es: 'Acumulable {n} veces',
    pt: 'Acumulável {n} vezes',
};
const TXT_ONE_TURN = { fr: '1 tour', en: '1 turn', de: '1 Runde', es: '1 turno', pt: '1 turno' };
const TXT_TURNS = { fr: '{n} tours', en: '{n} turns', de: '{n} Runden', es: '{n} turnos', pt: '{n} turnos' };
// A duration of -1: the buff holds until the fight ends (Zobal masks, Mutilation).
const TXT_WHOLE_FIGHT = {
    fr: "Jusqu'à la fin du combat",
    en: 'Until the end of the fight',
    de: 'Bis zum Ende des Kampfes',
    es: 'Hasta el final del combate',
    pt: 'Até o fim do combate',
};

const pick = (dict, lang) => dict?.[lang] || dict?.en || dict?.fr || '';

/**
 * What fires a buff and what gates it, as one label: "Après une attaque en
 * mêlée", "Fin de tour · Avec Porteur", or the Spells tab's "Direct" when
 * nothing does. Takes a detail line or a card's group (both carry
 * `triggerLabel` and `conditions`).
 */
export function buffConditionText(line, lang) {
    const trigger = pick(line?.triggerLabel, lang);
    const conditions = { conditions: line?.conditions || [] };
    const gate = formatSpellLineConditionText({ line: conditions, lang });
    if (trigger) return gate ? `${trigger} · ${gate}` : trigger;
    return formatSpellLineConditionText({ line: conditions, lang, fallback: 'direct' });
}

const durationText = (turns, lang) => {
    if (turns < 0) return pick(TXT_WHOLE_FIGHT, lang);
    if (turns === 1) return pick(TXT_ONE_TURN, lang);
    return pick(TXT_TURNS, lang).replace('{n}', String(turns));
};

const statDefFor = (id) => st.STATS.find((s) => s.id === id) || null;

/**
 * "+50 Force" — the value with the stat it lands on, or "-" when there is none.
 * A ladder quotes its first and last tier: "+1 → +30".
 */
const StatValue = ({ value, upTo = null, label }) => (value == null ? '-' : (
    <>
        {upTo != null ? `+${value} → +${upTo}` : `+${value}`}
        {label ? <span className="boost-details-val-label">{label}</span> : null}
    </>
));

/**
 * A boost's detail panel, laid out like a damage spell's (SpellDetails): one
 * row per characteristic it grants, labelled with the condition gating it —
 * "Direct" when nothing does, "Avec Rune", "À travers un portail", ... — then
 * the ordinary and critical values with the stat they raise ("+50 Force"), and
 * the cost and duration underneath.
 */
const BoostDetails = ({ row, lang = 'fr' }) => {
    // A boost holding several buffs quotes each one's stack cap on its line; a
    // single buff's goes in the footer with the cost and duration.
    const multiBuff = (row.groups || []).length > 1;
    // A ladder's cap is its tier count, not a stack, so it is not quoted.
    const singleCap = multiBuff || row.groups?.[0]?.isLadder ? 1 : (row.groups?.[0]?.maxCharges || 1);
    return (
    <div className="boost-details">
        <h5 className="boost-details-title">{pick(row.name, lang)}</h5>
        {pick(row.description, lang) ? (
            <p className="boost-details-description"><GameText text={pick(row.description, lang)} /></p>
        ) : null}
        {row.lines.length ? (
            <>
                <div className="boost-details-divider" role="presentation" />
                <table className="boost-details-table">
                    <thead>
                        <tr>
                            <th className="boost-details-th boost-details-th-type">{pick(HEADERS.type, lang)}</th>
                            <th className="boost-details-th boost-details-th-stat" scope="col" aria-label={pick(HEADERS.stat, lang)} />
                            <th className="boost-details-th">{pick(HEADERS.normal, lang)}</th>
                            <th className="boost-details-th boost-details-th-crit">{pick(HEADERS.critical, lang)}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {row.lines.map((line) => {
                            const stat = statDefFor(line.characteristic);
                            const label = pick(stat?.label, lang);
                            return (
                                <tr key={line.key} className="boost-details-tr">
                                    <td className="boost-details-td boost-details-type">
                                        {buffConditionText(line, lang)}
                                        {multiBuff && line.maxCharges > 1 && !line.isLadder ? (
                                            <span className="boost-details-type-stack">
                                                {pick(TXT_MAX_CHARGES, lang).replace('{n}', String(line.maxCharges))}
                                            </span>
                                        ) : null}
                                    </td>
                                    <td className="boost-details-td boost-details-stat">
                                        {stat?.icon ? (
                                            <img src={stat.icon} alt="" aria-hidden="true" className="boost-details-stat-icon" />
                                        ) : null}
                                    </td>
                                    <td className="boost-details-td boost-details-val">
                                        <StatValue value={line.value} upTo={line.maxValue} label={label} />
                                    </td>
                                    <td className="boost-details-td boost-details-val boost-details-crit">
                                        <StatValue value={line.critValue} label={label} />
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </>
        ) : null}
        <div className="boost-details-divider" role="presentation" />
        <div className="boost-details-footer">
            {row.isPassive ? (
                <span className="boost-details-meta"><strong>{pick(TXT_PASSIVE, lang)}</strong></span>
            ) : null}
            {!row.isPassive && row.apCost != null ? (
                <span className="boost-details-meta">{row.apCost} <strong>{pick(TXT_AP, lang)}</strong></span>
            ) : null}
            {singleCap > 1 ? (
                <span className="boost-details-meta">{pick(TXT_MAX_CHARGES, lang).replace('{n}', String(singleCap))}</span>
            ) : null}
            {row.duration ? (
                <span className="boost-details-meta">{durationText(row.duration, lang)}</span>
            ) : null}
        </div>
    </div>
    );
};

export default BoostDetails;
